<script lang="ts">
    import dayjs from 'dayjs'
    import { faAngleLeft } from '@fortawesome/free-solid-svg-icons'
    import { createEventDispatcher } from 'svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zgrid from '../_common/z/zgrid.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import ComissionsDetails from './comissions-details.svelte'
    import type { IComission, IComissionConsumption, IComissionGroup } from './comissions.interface'
    import { sumComissionParts } from '../../modules/use-cases/calc-comission'
    import Popover from '../_common/popover.svelte'

    const dispatch = createEventDispatcher()

    export let group: IComissionGroup

    // as ações de comissão (estornar/reativar, corrigir a venda de referência) continuam sendo por item —
    // o agrupamento é só de leitura. em vez de abrir um popover dentro do outro, troca o conteúdo deste
    // aqui mesmo, no padrão de step já usado no projeto.
    let selectedItem: IComission = null

    function forwardChange(event) {
        dispatch('change', event.detail)
    }

    // estorna/reativa a COBRANÇA do insumo na comissão do atendente. não desfaz o consumo nem devolve
    // estoque — para isso o caminho é remover o insumo do agendamento, não daqui.
    // sem guard de permissão próprio, igual ao estorno de comissão: a tela já é gateada por
    // permissions.comissions (show + entries.self/others).
    async function toggleConsumptionComission(item: IComissionConsumption) {
        try {
            await window.api.schedulers.appointmentConsumption.comissionsChangeStatus(
                item.appointmentConsumptionId,
                !!item.comissionCanceled, // estornado -> reativa; cobrado -> estorna
            )
            dispatch('change', group)
        } catch (error) {
            window.error(error)
        }
    }

    function showAppointment() {
        window.popover({ act: 'appointment-form', data: { appointmentId: group.appointmentId } })
    }

    function showPOS() {
        window.popover({ act: 'pos-form', data: { invoiceId: group.appointmentInvoiceId } })
    }

    function showPerson() {
        window.person('show', group.personId, 1)
    }

    // composição da comissão aberta parcela a parcela — nunca só o consolidado, pra permitir conferir de
    // onde saiu cada real sem precisar abrir item por item.
    // sumComissionParts exclui os itens cancelados de TODAS as parcelas de uma vez e devolve o total derivado
    // delas — então o que está exibido aqui sempre fecha, e bate com o rodapé do grid de itens ao lado (que
    // também ignora as linhas canceladas). Era somando parcela por parcela na mão que a conta desencontrava.
    $: parts = sumComissionParts(group.items.map((el) => el.details))

    let itemsGrid: GridClass = new GridClass()
    let consumptionGrid: GridClass = new GridClass()

    function loadGrids() {
        // taxa de meio de pagamento raramente se aplica — só ocupa coluna quando existe de fato
        const anyFeeDebt = group.items.some((el) => el.details?.comissionFeeDebt)
        const items = new GridClass()
        items.title = 'Itens do atendimento'
        items.subtitle = 'Composição da comissão de cada item.'
        items.fontSize = '13px'
        items.minWidth = '900px'
        items.cols.push(new GridCol({ content: 'Item' }))
        items.cols.push(new GridCol({ content: 'Status' }))
        items.cols.push(new GridCol({ content: 'Valor base', type: GridColTypeEnum.float_2, showTotal: true }))
        items.cols.push(new GridCol({ content: '(+) Percentual', type: GridColTypeEnum.float_2, showTotal: true }))
        items.cols.push(new GridCol({ content: '(+) Fixo', type: GridColTypeEnum.float_2, showTotal: true }))
        items.cols.push(new GridCol({ content: '(-) Descontos', type: GridColTypeEnum.float_2, showTotal: true }))
        if (anyFeeDebt) {
            items.cols.push(new GridCol({ content: '(-) Taxa meio pgto', type: GridColTypeEnum.float_2, showTotal: true }))
        }
        items.cols.push(new GridCol({ content: '(=) Comissão', type: GridColTypeEnum.float_2, showTotal: true }))
        items.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        for (const item of group.items) {
            const line = new GridLine()
            line.removed = item.details?.comissionCanceled || false
            line.slots.push({ content: item.service?.name || '-' })
            line.slots.push({ content: line.removed ? 'cancelada' : 'paga' })
            line.slots.push({ content: item.total })
            line.slots.push({ content: item.details?.comissionPercentage || 0 })
            line.slots.push({ content: item.details?.comissionFixed || 0 })
            line.slots.push({ content: -(item.details?.comissionCosts || 0) })
            if (anyFeeDebt) {
                line.slots.push({ content: -(item.details?.comissionFeeDebt || 0) })
            }
            line.slots.push({ content: item.comission })
            line.slots.push({
                content: [new GridButton({ label: 'Detalhes', callback: () => (selectedItem = item) })],
            })
            items.lines.push(line)
        }
        itemsGrid = items

        const consumption = new GridClass()
        consumption.title = 'Insumos consumidos'
        consumption.subtitle = 'Custo descontado da comissão deste atendente. Definido no cadastro do insumo (Custo unitário).'
        consumption.fontSize = '13px'
        consumption.cols.push(new GridCol({ content: 'Insumo' }))
        consumption.cols.push(new GridCol({ content: 'Quantidade', type: GridColTypeEnum.float_2 }))
        consumption.cols.push(new GridCol({ content: 'Status' }))
        consumption.cols.push(new GridCol({ content: 'Custo descontado', type: GridColTypeEnum.float_2, showTotal: true }))
        consumption.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        for (const item of group.consumption) {
            const line = new GridLine()
            // estornado: exibe o valor cheio riscado (getTotal ignora a linha, então o rodapé já mostra só o
            // que será cobrado). o insumo continua consumido — o estorno não mexe no estoque.
            line.removed = item.comissionCanceled || false
            line.slots.push({ content: item.service?.name || '-' })
            line.slots.push({ content: item.quantity })
            line.slots.push({ content: line.removed ? 'estornado' : 'cobrado' })
            line.slots.push({ content: -item.comissionCost })
            line.slots.push({
                content: [
                    new GridButton({
                        label: line.removed ? 'Reativar cobrança' : 'Estornar insumo',
                        callback: () => toggleConsumptionComission(item),
                    }),
                ],
            })
            consumption.lines.push(line)
        }
        consumptionGrid = consumption
    }

    $: if (group) loadGrids()
</script>

{#if selectedItem}
    <Popover title="Detalhes do atendimento" width="1280px" on:close={() => (selectedItem = null)}>
        <ComissionsDetails detail={selectedItem} on:change={forwardChange} />
    </Popover>
{:else}
    <Zact>
        <div class="spacer" />
        <Zbutton on:click={showAppointment} label="Agendamento" />
        {#if group.appointmentInvoiceId}
            <Zbutton on:click={showPOS} label="Venda" />
        {/if}
        <Zbutton on:click={showPerson} label="Pessoa" />
    </Zact>
    <div class="m-3">
        <ZContainer>
            <Zframe size={8} title="Atendimento" headline="">
                <div class="show-details -content">
                    <div class="row">
                        <div class="col"><span class="title-badge">Cliente</span><span>{group.person?.name || '-'}</span></div>
                    </div>
                    <div class="row">
                        <div class="col"><span class="title-badge">Data</span><span>{dayjs(group.date).format('DD/MM/YYYY HH:mm:ss')}</span></div>
                    </div>
                    <div class="row">
                        <div class="col"><span class="title-badge">Atendente</span><span>{group.user?.userName || '-'}</span></div>
                    </div>
                    <div class="row">
                        <div class="col"><span class="title-badge">Valor base</span><span>{window.api.utils.utils.toReal(group.total)}</span></div>
                    </div>
                </div>
            </Zframe>
            <Zframe size={4} title="Comissão do atendimento" headline="">
                <div class="show-details -content">
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">(+) Percentual</span>
                            <span>{window.api.utils.utils.toReal(parts.percentage)}</span>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">(+) Fixo</span>
                            <span>{window.api.utils.utils.toReal(parts.fixed)}</span>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">(-) Descontos</span>
                            <span>{window.api.utils.utils.toReal(-parts.costs)}</span>
                        </div>
                    </div>
                    {#if parts.feeDebt}
                        <div class="row">
                            <div class="col">
                                <span class="title-badge">(-) Taxa meio pgto</span>
                                <span>{window.api.utils.utils.toReal(-parts.feeDebt)}</span>
                            </div>
                        </div>
                    {/if}
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">(-) Insumos consumidos</span>
                            <span>{window.api.utils.utils.toReal(-group.consumptionCost)}</span>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <span class="title-badge">(=) Líquido</span>
                            <span class:negative={group.comission < 0}>{window.api.utils.utils.toReal(group.comission)}</span>
                        </div>
                    </div>
                    {#if group.comission < 0}
                        <div class="row">
                            <div class="col negative-note">
                                O custo dos insumos ficou maior que a comissão deste atendimento. Confira o custo unitário no cadastro do insumo.
                            </div>
                        </div>
                    {/if}
                </div>
            </Zframe>
        </ZContainer>

        <Zgrid class="h-space" grid={itemsGrid} />
        {#if group.consumption.length > 0}
            <Zgrid class="h-space" grid={consumptionGrid} />
        {/if}
    </div>
{/if}

<style>
    .show-details .row {
        font-size: 14px;
        padding: 8px;
        border-bottom: 1px solid #99999918;
    }
    .negative {
        color: #dc3545;
    }
    .negative-note {
        font-size: 12px;
        color: #dc3545;
        opacity: 0.9;
    }
</style>
